import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { ShieldCheck, Loader2, Mail, Lock, CheckCircle2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  getBgvCaseFromToken, sendBgvOtp, verifyBgvOtp,
  submitBgvConsent, submitBgvForm,
} from '../api';

const CONSENT_TEXT = `I hereby consent to T&N Business Services conducting Background Verification checks on
the personal, educational, employment, address, and criminal-history information I have
provided. I understand that these details will be shared with authorised verification
partners solely for this purpose and treated confidentially per applicable privacy laws.`;

const BGVCandidatePortal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [step, setStep] = useState('otp'); // 'otp' | 'consent' | 'form' | 'done'
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [consentBusy, setConsentBusy] = useState(false);
  const [formBusy, setFormBusy] = useState(false);
  const [form, setForm] = useState({
    pan: '', aadhaar_last4: '', dob: '',
    current_address: '', permanent_address: '',
    education: [{ institution: '', qualification: '', course: '', passing_year: '' }],
    employment: [{ employer: '', designation: '', start_date: '', end_date: '', reason_for_leaving: '' }],
  });

  const bootstrap = async () => {
    setLoading(true);
    try {
      const { data } = await getBgvCaseFromToken(token);
      setCaseData(data);
      setTypedName(data.candidate_name || '');
      if (data.form) setForm(prev => ({ ...prev, ...data.form,
        education: data.form.education?.length ? data.form.education : prev.education,
        employment: data.form.employment?.length ? data.form.employment : prev.employment,
      }));
      if (data.otp_verified && !data.consent?.given) setStep('consent');
      else if (data.otp_verified && data.consent?.given && data.status === 'candidate_action_required') setStep('form');
      else if (data.otp_verified && data.status !== 'candidate_action_required' && data.consent?.given) setStep('done');
      else setStep('otp');
    } catch (err) {
      setLinkError(err.response?.data?.detail || 'Invalid or expired link');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { bootstrap(); /* eslint-disable-next-line */ }, [token]);

  const handleSendOtp = async () => {
    setOtpBusy(true);
    try {
      await sendBgvOtp(token);
      setOtpSent(true);
      toast.success(`OTP sent to ${caseData.candidate_email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally { setOtpBusy(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otp) return;
    setVerifyBusy(true);
    try {
      await verifyBgvOtp(token, otp);
      toast.success('Verified');
      await bootstrap();
      setStep('consent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OTP verification failed');
    } finally { setVerifyBusy(false); }
  };

  const handleSubmitConsent = async () => {
    if (!typedName.trim()) { toast.error('Please type your full name to sign consent'); return; }
    setConsentBusy(true);
    try {
      await submitBgvConsent(token, typedName.trim());
      toast.success('Consent recorded');
      setStep('form');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit consent');
    } finally { setConsentBusy(false); }
  };

  const handleSubmitForm = async () => {
    if (!form.pan?.trim() || !form.dob) {
      toast.error('PAN and DOB are required');
      return;
    }
    setFormBusy(true);
    try {
      await submitBgvForm(token, form);
      toast.success('Details submitted. Our team will take it from here.');
      setStep('done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit form');
    } finally { setFormBusy(false); }
  };

  const addEducation = () => setForm(f => ({
    ...f, education: [...f.education, { institution: '', qualification: '', course: '', passing_year: '' }],
  }));
  const rmEducation = (i) => setForm(f => ({ ...f, education: f.education.filter((_, idx) => idx !== i) }));
  const addEmp = () => setForm(f => ({
    ...f, employment: [...f.employment, { employer: '', designation: '', start_date: '', end_date: '', reason_for_leaving: '' }],
  }));
  const rmEmp = (i) => setForm(f => ({ ...f, employment: f.employment.filter((_, idx) => idx !== i) }));

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="animate-spin text-indigo-600" size={32} />
    </div>
  );

  if (linkError) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="max-w-md">
        <CardContent className="pt-6 text-center">
          <ShieldCheck className="mx-auto text-rose-500" size={40} />
          <h1 className="text-xl font-bold mt-3">Invalid link</h1>
          <p className="text-slate-600 text-sm mt-2">{linkError}</p>
          <p className="text-slate-500 text-xs mt-4">
            Please contact the HR team who initiated your background check for a fresh link.
          </p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-6" data-testid="bgv-portal-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center pt-8">
          <ShieldCheck className="mx-auto text-indigo-600" size={40} />
          <h1 className="text-3xl font-bold mt-3 text-slate-900">Background Verification</h1>
          <p className="text-slate-500 text-sm mt-1">
            Case <span className="font-mono text-slate-700">{caseData?.case_number}</span> ·{' '}
            {caseData?.candidate_name}
          </p>
        </div>

        {/* Progress indicator */}
        <div className="flex justify-center gap-1 text-xs">
          {['otp', 'consent', 'form', 'done'].map((s, i) => (
            <div key={s} className={`px-3 py-1 rounded-full ${
              step === s ? 'bg-indigo-600 text-white'
              : ['otp','consent','form','done'].indexOf(step) > i
                ? 'bg-emerald-500 text-white'
                : 'bg-slate-200 text-slate-500'
            }`}>
              {i + 1}. {s.toUpperCase()}
            </div>
          ))}
        </div>

        {/* OTP */}
        {step === 'otp' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Mail size={18} /> Email Verification</CardTitle>
              <CardDescription>
                We&apos;ll send a one-time code to <strong>{caseData?.candidate_email}</strong> to
                confirm this is you.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!otpSent ? (
                <Button onClick={handleSendOtp} disabled={otpBusy} data-testid="send-otp-btn">
                  {otpBusy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Mail size={14} className="mr-2" />}
                  Send verification code
                </Button>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Enter 6-digit code</Label>
                    <Input value={otp} maxLength={6}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g,''))}
                      className="tracking-widest text-center text-xl"
                      data-testid="otp-input" placeholder="•  •  •  •  •  •" />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleVerifyOtp} disabled={verifyBusy || otp.length < 6}
                      data-testid="verify-otp-btn">
                      {verifyBusy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Lock size={14} className="mr-2" />}
                      Verify
                    </Button>
                    <Button variant="outline" onClick={handleSendOtp} disabled={otpBusy}>
                      Resend code
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Consent */}
        {step === 'consent' && (
          <Card>
            <CardHeader>
              <CardTitle>Consent</CardTitle>
              <CardDescription>Please read carefully before signing.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 leading-relaxed">
                {CONSENT_TEXT}
              </div>
              <div className="space-y-2">
                <Label>Sign by typing your full name *</Label>
                <Input value={typedName} onChange={(e) => setTypedName(e.target.value)}
                  className="italic" data-testid="consent-name-input" />
              </div>
              <Button onClick={handleSubmitConsent} disabled={consentBusy}
                data-testid="submit-consent-btn">
                {consentBusy ? <Loader2 size={14} className="animate-spin mr-2" /> : <CheckCircle2 size={14} className="mr-2" />}
                I consent
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Form */}
        {step === 'form' && (
          <Card>
            <CardHeader>
              <CardTitle>Your Details</CardTitle>
              <CardDescription>
                All fields help us verify you accurately. PAN and DOB are required.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>PAN *</Label>
                  <Input value={form.pan}
                    onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
                    maxLength={10} className="uppercase" data-testid="pan-input" />
                </div>
                <div className="space-y-2">
                  <Label>Last 4 digits of Aadhaar</Label>
                  <Input value={form.aadhaar_last4}
                    onChange={(e) => setForm({ ...form, aadhaar_last4: e.target.value.replace(/\D/g,'').slice(0,4) })}
                    maxLength={4} />
                </div>
                <div className="space-y-2">
                  <Label>Date of Birth *</Label>
                  <Input type="date" value={form.dob}
                    onChange={(e) => setForm({ ...form, dob: e.target.value })}
                    data-testid="dob-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Current Address</Label>
                <Textarea value={form.current_address}
                  onChange={(e) => setForm({ ...form, current_address: e.target.value })} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Permanent Address</Label>
                <Textarea value={form.permanent_address}
                  onChange={(e) => setForm({ ...form, permanent_address: e.target.value })} rows={2} />
              </div>

              {/* Education */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-base">Education</Label>
                  <Button variant="ghost" size="sm" onClick={addEducation}>
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </div>
                {form.education.map((e, i) => (
                  <div key={i} className="grid md:grid-cols-4 gap-2 border border-slate-200 rounded-md p-3 relative">
                    <Input placeholder="Institution" value={e.institution}
                      onChange={(ev) => setForm(f => { const arr=[...f.education]; arr[i]={...arr[i],institution:ev.target.value}; return {...f,education:arr}; })} />
                    <Input placeholder="Qualification" value={e.qualification}
                      onChange={(ev) => setForm(f => { const arr=[...f.education]; arr[i]={...arr[i],qualification:ev.target.value}; return {...f,education:arr}; })} />
                    <Input placeholder="Course" value={e.course}
                      onChange={(ev) => setForm(f => { const arr=[...f.education]; arr[i]={...arr[i],course:ev.target.value}; return {...f,education:arr}; })} />
                    <div className="flex gap-1">
                      <Input placeholder="Year" value={e.passing_year}
                        onChange={(ev) => setForm(f => { const arr=[...f.education]; arr[i]={...arr[i],passing_year:ev.target.value}; return {...f,education:arr}; })} />
                      {form.education.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => rmEducation(i)}>
                          <Trash2 size={14} className="text-rose-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Employment */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-base">Employment History</Label>
                  <Button variant="ghost" size="sm" onClick={addEmp}>
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </div>
                {form.employment.map((e, i) => (
                  <div key={i} className="grid md:grid-cols-2 gap-2 border border-slate-200 rounded-md p-3">
                    <Input placeholder="Employer" value={e.employer}
                      onChange={(ev) => setForm(f => { const arr=[...f.employment]; arr[i]={...arr[i],employer:ev.target.value}; return {...f,employment:arr}; })} />
                    <Input placeholder="Designation" value={e.designation}
                      onChange={(ev) => setForm(f => { const arr=[...f.employment]; arr[i]={...arr[i],designation:ev.target.value}; return {...f,employment:arr}; })} />
                    <Input type="date" placeholder="Start" value={e.start_date}
                      onChange={(ev) => setForm(f => { const arr=[...f.employment]; arr[i]={...arr[i],start_date:ev.target.value}; return {...f,employment:arr}; })} />
                    <Input type="date" placeholder="End" value={e.end_date}
                      onChange={(ev) => setForm(f => { const arr=[...f.employment]; arr[i]={...arr[i],end_date:ev.target.value}; return {...f,employment:arr}; })} />
                    <Input placeholder="Reason for leaving" className="md:col-span-2" value={e.reason_for_leaving}
                      onChange={(ev) => setForm(f => { const arr=[...f.employment]; arr[i]={...arr[i],reason_for_leaving:ev.target.value}; return {...f,employment:arr}; })} />
                    {form.employment.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => rmEmp(i)}
                        className="text-rose-500 md:col-span-2 justify-start w-fit">
                        <Trash2 size={14} className="mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              <Button onClick={handleSubmitForm} disabled={formBusy} size="lg" className="w-full"
                data-testid="submit-form-btn">
                {formBusy ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                Submit for verification
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Done */}
        {step === 'done' && (
          <Card>
            <CardContent className="pt-6 text-center space-y-3">
              <CheckCircle2 className="mx-auto text-emerald-500" size={40} />
              <h2 className="text-xl font-bold text-slate-900">Thank you!</h2>
              <p className="text-slate-600 text-sm">
                Your details have been submitted. Our verification team will complete the checks and
                revert to you if anything else is needed.
              </p>
              <Badge className="bg-emerald-100 text-emerald-800">Case status: {caseData?.status}</Badge>
            </CardContent>
          </Card>
        )}

        <div className="text-center text-xs text-slate-400 pb-6">
          Powered by T&amp;N HRMS · Your data is encrypted and used solely for background verification.
        </div>
      </div>
    </div>
  );
};

export default BGVCandidatePortal;
